"use client";

import Image from "./image";
import Link from "./router-link";
import { usePathname } from "../lib/router";
import {
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  Send,
  X,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { AnimatedBackground } from "./core/animated-background";
import { Cursor } from "./core/cursor";
import { usePageTransition } from "./page-transition";
import { navItems, services, solutionCards } from "../lib/site-data";
import { siteConfig } from "../lib/site-config";
import { AiCustomerService } from "./ai-customer-service";
import { CustomerChat } from "./customer-chat";
import { ImCustomerChat } from "./im-customer-chat";

export function Header() {
  const pathname = usePathname();
  const { navigate } = usePageTransition();
  const activeHref = navItems.find((item) =>
    item.href === "/" ? pathname === "/" : pathname.startsWith(item.href),
  )?.href;
  const activeIndex = navItems.findIndex((item) => item.href === activeHref);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`site-header ${scrolled ? "is-scrolled" : ""}`}>
      <div className="header-inner">
        <Link
          href="/"
          className="brand"
          aria-label="增长深度 GrowDeep 首页"
          onNavigate={(event) => {
            event.preventDefault();
            navigate("/", activeIndex > 0 ? -1 : 1);
          }}
        >
          <Image
            className="brand-logo brand-logo-white"
            src={siteConfig.brand.logo}
            alt={siteConfig.brand.name}
            width={186}
            height={32}
            priority
          />
        </Link>
        <nav
          className={`main-nav ${open ? "is-open" : ""}`}
          aria-label="主导航"
        >
          <AnimatedBackground
            key={pathname}
            defaultValue={activeHref ?? ""}
            enableHover
            transition={{ type: "spring", bounce: 0.2, duration: 0.3 }}
            className="nav-hover-background"
          >
            {navItems.map((item) => {
              const active = activeHref === item.href;
              const targetIndex = navItems.findIndex(
                (navItem) => navItem.href === item.href,
              );
              return (
                <Link
                  className={active ? "active" : ""}
                  data-id={item.href}
                  href={item.href}
                  key={item.href}
                  onClick={() => setOpen(false)}
                  onNavigate={(event) => {
                    event.preventDefault();
                    navigate(item.href, targetIndex < activeIndex ? -1 : 1);
                  }}
                >
                  {item.label}
                </Link>
              );
            })}
          </AnimatedBackground>
          <Link
            className="mobile-diagnosis"
            href="/contact"
            onClick={() => setOpen(false)}
            onNavigate={(event) => {
              event.preventDefault();
              navigate("/contact", 1);
            }}
          >
            预约增长诊断 <ArrowUpRight size={16} />
          </Link>
        </nav>
        <Link
          className="button button-light header-cta"
          href="/contact"
          onNavigate={(event) => {
            event.preventDefault();
            navigate("/contact", 1);
          }}
        >
          预约增长诊断
        </Link>
        <button
          className="nav-toggle"
          type="button"
          aria-label={open ? "关闭导航" : "打开导航"}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="site-footer">
      <div className="footer-inner">
        <div className="footer-brand">
          <Image
            className="brand-logo brand-logo-white"
            src={siteConfig.brand.logo}
            alt={siteConfig.brand.name}
            width={192}
            height={34}
          />
          <p>企业品牌增长与产品出海增长服务商。让品牌被看见，让客户找上门。</p>
        </div>
        <div className="footer-column">
          <h3>解决方案</h3>
          {solutionCards.map((solution) => (
            <Link href={`/solutions#${solution.slug}`} key={solution.slug}>
              {solution.title}
            </Link>
          ))}
        </div>
        <div className="footer-column">
          <h3>服务</h3>
          {services.map((service) => (
            <Link href={`/services#${service.slug}`} key={service.slug}>
              {service.title}
            </Link>
          ))}
          <Link href="/services#combination">合作流程</Link>
          <Link href="/pricing">套餐价格</Link>
          <Link href="/contact">增长诊断</Link>
        </div>
        <div className="footer-column">
          <h3>增长洞察</h3>
          <Link href="/insights#articles">全部文章</Link>
          <Link href="/cases">客户案例</Link>
        </div>
        <div className="footer-column">
          <h3>关于我们</h3>
          <Link href="/about">公司介绍</Link>
          <Link href="/about#method">方法论</Link>
          <Link href="/about#team">团队</Link>
        </div>
        <div className="footer-contact">
          <h3>联系顾问</h3>
          <div>
            <Phone size={15} />
            {siteConfig.contact.phone.display}
          </div>
          <div>
            <Mail size={15} />
            {siteConfig.contact.email}
          </div>
          <span>
            <MapPin size={15} />
            {siteConfig.contact.address}
          </span>
        </div>
        <div className="footer-qr">
          <Image
            src={siteConfig.contact.qrCode.src}
            alt={siteConfig.contact.qrCode.alt}
            width={92}
            height={92}
          />
          <span>
            {siteConfig.contact.qrCode.label}
            <br />
            {siteConfig.contact.qrCode.description}
          </span>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© 2026 增长深度 GrowDeep</span>
        <Link href="/privacy">隐私政策</Link>
        <Link href="/terms">服务条款</Link>
      </div>
    </footer>
  );
}

export function FloatingContact() {
  const pathname = usePathname();
  const [open, setOpen] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatPrompt, setChatPrompt] = useState<string | undefined>();
  const [imChatOpen, setImChatOpen] = useState(false);

  const openChat = (prompt?: string) => {
    setChatPrompt(prompt);
    setChatOpen(true);
  };

  if (pathname === "/contact") return null;

  return (
    <>
      <div className="floating-contact-wrap">
        <AnimatePresence initial={false} mode="wait">
          {open ? (
            <motion.aside
              key="contact-panel"
              id="floating-contact-panel"
              className="floating-contact"
              aria-label="联系增长深度"
              initial={{ opacity: 0, x: 64 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 64 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <button
                className="floating-contact-control"
                type="button"
                aria-label="收起联系栏"
                title="收起联系栏"
                onClick={() => setOpen(false)}
              >
                <ChevronRight aria-hidden="true" size={16} />
              </button>
              <button
                className="floating-contact-button"
                type="button"
                onClick={() => setImChatOpen(true)}
              >
                <Send aria-hidden="true" size={20} />
                <span>咨询</span>
              </button>
              <div className="floating-contact-item">
                <button
                  className="floating-contact-button"
                  type="button"
                  aria-describedby="floating-wechat-card"
                >
                  <MessageCircle aria-hidden="true" size={20} />
                  <span>微信</span>
                </button>
                <div
                  className="floating-contact-card"
                  id="floating-wechat-card"
                >
                  <Image
                    src={siteConfig.contact.qrCode.src}
                    alt={siteConfig.contact.qrCode.alt}
                    width={128}
                    height={128}
                  />
                  <strong>微信沟通</strong>
                  <span>{siteConfig.contact.wechat}</span>
                </div>
              </div>
              <div className="floating-contact-item">
                <div
                  className="floating-contact-button"
                  aria-describedby="floating-phone-card"
                >
                  <Phone aria-hidden="true" size={20} />
                  <span>电话</span>
                </div>
                <div
                  className="floating-contact-card floating-contact-phone-card"
                  id="floating-phone-card"
                >
                  <strong>{siteConfig.contact.phone.display}</strong>
                  <span>{siteConfig.contact.businessHours}</span>
                </div>
              </div>
              <AiCustomerService onOpenChat={openChat} />
            </motion.aside>
          ) : (
            <motion.button
              key="contact-trigger"
              className="floating-contact-trigger"
              type="button"
              aria-label="展开联系栏"
              aria-controls="floating-contact-panel"
              title="展开联系栏"
              initial={{ opacity: 0, x: 44 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 44 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              onClick={() => setOpen(true)}
            >
              <ChevronLeft aria-hidden="true" size={18} />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
      <CustomerChat
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        initialPrompt={chatPrompt}
      />
      <ImCustomerChat open={imChatOpen} onClose={() => setImChatOpen(false)} />
    </>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="section-heading">
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      {action && <div className="section-action">{action}</div>}
    </div>
  );
}

export function CTA({
  title = "让好产品被看见，让客户主动找上门。",
  description = "预约一次初步增长诊断，梳理品牌、网站、内容、搜索与获客路径的优先事项。",
}: {
  title?: string;
  description?: string;
}) {
  const [isHovering, setIsHovering] = useState(false);

  return (
    <Link
      className="cta-band section-shell"
      href="/contact"
      aria-label="预约增长诊断"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="cta-media" aria-hidden="true" />
      <Cursor
        attachToParent
        transition={{ ease: "easeInOut", duration: 0.15 }}
        variants={{
          initial: { scale: 0.3, opacity: 0 },
          animate: { scale: 1, opacity: 1 },
          exit: { scale: 0.3, opacity: 0 },
        }}
      >
        <motion.div
          animate={{
            width: isHovering ? 112 : 18,
            height: isHovering ? 38 : 18,
          }}
          className="cta-cursor-pill"
        >
          <AnimatePresence initial={false}>
            {isHovering && (
              <motion.span
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.6 }}
                className="cta-cursor-label"
              >
                预约诊断 <ArrowUpRight size={15} />
              </motion.span>
            )}
          </AnimatePresence>
        </motion.div>
      </Cursor>
      <div className="cta-content">
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
    </Link>
  );
}

export function PageHero({
  eyebrow,
  title,
  description,
  image = "/assets/earth.jpg",
  light = false,
  compact = false,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  image?: string;
  light?: boolean;
  compact?: boolean;
}) {
  return (
    <section
      className={`page-hero ${light ? "page-hero-light" : ""} ${compact ? "page-hero-compact" : ""}`}
    >
      <div
        className="page-hero-media"
        style={{ backgroundImage: `url(${image})` }}
      />
      <div className="page-hero-inner">
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
    </section>
  );
}
